<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <AgGrid class="flex-grow-1" ref="alertGridRef" :fetchUrl="alertFetchUrl"
        :columnDefs="alertColumnDefs" :gridParams="alertGridParams" :stepLoad="alertStepLoad"
        :refresh="alertRefresh" :listValues="alertListValues" :domLayout="domLayout"/>
    </div>
</template>

<script setup>
    import { computed, inject, ref, watch } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import { toast } from 'vue3-toastify';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';

    const props = defineProps({
        customerReference: {
            type: String,
            default: null,
        },
    });
    const emit = defineEmits(['pending-changes']);

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');

    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const domLayout = 'normal';

    const alertFetchUrl = 'GetAlerts';
    const alertListValues = {
        roles:[],
    };

    const alertColumnDefs = [
        {
            field: 'applicationDomain',
            headerName: '',
        },
        {
            field: 'type',
            headerName: translate('type', 'column'),
        },
        {
            field: 'name',
            headerName: translate('name', 'column'),
            minWidth: 250,
        },
        {
            field: 'menu',
            headerName: translate('menu', 'column'),
            hide: true,
        },
        {
            field: 'description',
            headerName: translate('description', 'column'),
            pinned: 'left',
        },
        {
            field: 'startDate',
            headerName: translate('startDate', 'column'),
            type: 'date',
            editable: (params) => {
                return params.data.applicationDomain != 'null' && params.data.applicationDomain == 'Assortiment';
            },
        },
        {
            field: 'delayHour',
            headerName: translate('delay', 'column'),
            type: 'delayHour',
            editable: (params) => {
                return params.data.applicationDomain != 'null' && params.data.applicationDomain == 'Assortiment';
            },
        },
        {
            field: 'shortMsg',
            headerName: translate('shortMsg', 'column'),
        },
        {
            field: 'roles',
            headerName: translate('roles', 'column'),
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: (params) => {
                return {
                    values: alertListValues.roles.map(role => role.id),
                    multiSelect: true,
                    formatValue: (id) => {
                        let role = alertListValues.roles.filter(r => r.id == id).first();
                        return role ? role.name : id;
                    },
                };
            },
            //display selected roles as "name, name ..."
            valueFormatter: (params) => {
                if (!Array.isArray(params.value))
                    return params.value;

                return alertListValues.roles.filter(role => params.value.indexOf(role.id) > -1).map(role => role.name).join(', ');
            },
            //column is not an object but an array so no need to define a celldatatype
            cellDataType: false,
            editable: (params) => {
                return params.data.applicationDomain != 'null' && params.data.applicationDomain == 'Assortiment' || params.data.applicationDomain == null && params.data.name == 'ALERT_SHORT_13';
            },
            minWidth: 200,
        },
        {
            field: 'mode',
            headerName: translate('mode', 'column'),
        },
        {
            field: 'activeFlag',
            headerName: translate('active', 'column'),
            type:'booleanEditable',
            editable: true,
            minWidth: 100,
        },
    ];
    const alertGridParams = {
        token: userStore.getUser().token,
        customerReference: props.customerReference,
    };

    const alertStepLoad = ref(false);
    const alertRefresh = ref(false);

    const roles = ref([]);
    const rowsUpdated = ref([]);

    const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);

    watch(hasPendingChanges, (value) => {
        emit('pending-changes', value);
    });

    getRoles();

    function getRoles() {
        let data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
        };

        $.getData(urlWebAPI + 'GetRoles', data)
            .then((data) => {
                roles.value = data;
                alertListValues['roles'] = data;

                alertRefresh.value = true;

                setTimeout(() => {
                    alertRefresh.value = false;
                }, 1000);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetRoles data.');
            });
    }

    function save() {
        let data = {
            token: userStore.getUser().token,
            customerReference: props.customerReference,
            alertInfos: rowsUpdated.value,
        };

        $.postData(urlWebAPI + 'SaveAlerts', data)
            .then(() => {
                rowsUpdated.value = [];
                toast.success(translate('customerParamsWellSaved', 'toast'));
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveAlerts data.');
            });
    }

    function undo() {
        var gridApi = controlStore.getDisplayedGrid().api;

        for (var i = 0; i < gridApi.getCurrentUndoSize() + 1; i++)
            setTimeout(() => {
                gridApi.undoCellEditing();
            }, 10 * i);

        gridApi.redrawRows();
        rowsUpdated.value = [];
    }

    // ViewManageCustomer.vue calls undo()/save() through a template ref, and AgGrid.vue reaches
    // into rowsUpdated through the $parent chain — both only see what defineExpose() exposes.
    defineExpose({ undo, save, rowsUpdated });
</script>
